import { useCallback, useEffect, useRef, useState } from "react";

import { useDashboardRealtime } from "../../hooks/useDashboardRealtime";
import {
  calculateBurnRateUsdPerHr,
  getTokenDeltaEntryForInstance,
  RECALCULATE_INTERVAL_MS,
  shouldTrackBurnRate,
  type TokenDeltaEntry,
} from "./useBurnRateController";

// ---------------------------------------------------------------------------
// Hook: useBurnRate
// Computes realtime $/hr burn rate from instance_activity token_delta events.
// Uses a fixed 5-minute sliding denominator so the value appears as soon as a
// step finishes without extrapolating one burst across a tiny elapsed span.
// ---------------------------------------------------------------------------

export function useBurnRate(
  instanceId: string | null,
  instanceStatus?: string,
): number | null {
  const [burnRateUsdPerHr, setBurnRateUsdPerHr] = useState<number | null>(null);
  const bufferRef = useRef<TokenDeltaEntry[]>([]);
  const { subscribe } = useDashboardRealtime();
  const instanceIdRef = useRef(instanceId);
  instanceIdRef.current = instanceId;

  // Reset when instance changes.
  useEffect(() => {
    bufferRef.current = [];
    setBurnRateUsdPerHr(null);
  }, [instanceId]);

  // Clear buffer when actor parks or is killed — avoids extrapolating a huge
  // $/hr rate from a short burst of tokens that arrived before the actor parked.
  useEffect(() => {
    if (!shouldTrackBurnRate(instanceStatus)) {
      bufferRef.current = [];
      setBurnRateUsdPerHr(null);
    }
  }, [instanceStatus]);

  const recalculate = useCallback((nowMs = Date.now()) => {
    const { entries, burnRateUsdPerHr: nextBurnRateUsdPerHr } =
      calculateBurnRateUsdPerHr(bufferRef.current, nowMs);
    bufferRef.current = entries;
    setBurnRateUsdPerHr(nextBurnRateUsdPerHr);
  }, []);

  useEffect(() => {
    if (!instanceId || !shouldTrackBurnRate(instanceStatus)) {
      return;
    }

    const interval = window.setInterval(() => {
      recalculate();
    }, RECALCULATE_INTERVAL_MS);

    return () => {
      window.clearInterval(interval);
    };
  }, [instanceId, instanceStatus, recalculate]);

  useEffect(() => {
    if (!instanceId || !shouldTrackBurnRate(instanceStatus)) {
      return;
    }

    const unsubscribe = subscribe(
      (message) => {
        const entry = getTokenDeltaEntryForInstance(
          message,
          instanceIdRef.current,
        );
        if (!entry) {
          return;
        }

        bufferRef.current.push(entry);
        recalculate();
      },
      { topic: "internalEventTopic" },
    );

    return unsubscribe;
  }, [instanceId, instanceStatus, subscribe, recalculate]);

  return burnRateUsdPerHr;
}
